/**
 * Category icon system for camp card placeholders
 * Uses Heroicons (Tailwind's icon library) for consistency
 */

import {
  PaintBrushIcon,
  BeakerIcon,
  FireIcon,
  AcademicCapIcon,
  TrophyIcon,
  FlagIcon,
} from '@heroicons/react/24/outline';

export interface CategoryIconConfig {
  icon: React.ReactNode;
  name: string;
}

// Category icons using Heroicons (Tailwind's icon system)
export const CATEGORY_ICONS: Record<string, CategoryIconConfig> = {
  sports: {
    name: 'Sports & Fitness',
    icon: <TrophyIcon className="w-full h-full" />, // Classic sports/achievement
  },
  art: {
    name: 'Arts & Creativity',
    icon: <PaintBrushIcon className="w-full h-full" />,
  },
  stem: {
    name: 'STEM & Innovation',
    icon: <BeakerIcon className="w-full h-full" />,
  },
  adventure: {
    name: 'Adventure & Nature',
    icon: <FireIcon className="w-full h-full" />,
  },
  academic: {
    name: 'Academic Enrichment',
    icon: <AcademicCapIcon className="w-full h-full" />,
  },
  leadership: {
    name: 'Leadership & Life Skills',
    icon: <FlagIcon className="w-full h-full" />, // Leading, direction, goals
  },
};

// Primary category names (matching filter pills)
const PRIMARY_CATEGORIES = [
  'Academic Enrichment',
  'Adventure & Nature',
  'Animal Encounters',
  'Arts & Creativity',
  'Cultural & Language Immersion',
  'Leadership & Life Skills',
  'Multi-Activity',
  'Special Interests & Hobbies',
  'Sports & Fitness',
  'STEM & Innovation',
  'Wellness & Mindfulness',
];

// Keywords to match PRIMARY categories only
const CATEGORY_KEYWORDS: Record<string, string[]> = {
  sports: ['sports & fitness', 'sport', 'athletic'],
  art: ['arts & creativity', 'art', 'creative'],
  stem: ['stem & innovation', 'stem', 'science', 'technology', 'innovation'],
  adventure: ['adventure & nature', 'adventure', 'nature', 'outdoor'],
  academic: ['academic enrichment', 'academic', 'enrichment'],
  leadership: ['leadership & life skills', 'leadership', 'life skills'],
};

/**
 * Get matching icons for camp categories
 */
export function getCategoryIcons(categories: Array<{name: string}> | undefined): CategoryIconConfig[] {
  if (!categories || categories.length === 0) {
    return [];
  }

  const matchedIcons: CategoryIconConfig[] = [];
  const usedKeys = new Set<string>();

  // Check each category against keywords
  for (const category of categories) {
    const categoryName = category.name.toLowerCase();
    
    for (const [key, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (!usedKeys.has(key) && keywords.some(keyword => categoryName.includes(keyword))) {
        matchedIcons.push(CATEGORY_ICONS[key]);
        usedKeys.add(key);
        break;
      }
    }
  }

  return matchedIcons;
}
